import React, { useCallback, useEffect, useState } from 'react';
import styles from '../../styles/ReporteEstimacionAlumnadoStyles/ReporteEstimacionAlumnado.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import FiltrosReporte from './FiltrosReporte';
import TablaReporte from './TablaReporte';
import { getReporteEstimacionAlumnado } from '../../api/reporteEstimacionAlumnado';
import { exportReporteEstimacionAlumnadoXLS } from '../../utils/exportReporteEstimacionAlumnadoXLS';
import { getCampus } from '../../api/campus';
import { getInstitutions } from '../../api/institutions';

function ReporteEstimacionAlumnadoScreen() {
  const { isCollapsed } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [campusList, setCampusList] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [contexto, setContexto] = useState({
    ciclo: '',
    campus: '',
    seccion: '',
    programa: '',
    institucion: '',
  });

  const [filters, setFilters] = useState({
    id_ciclo_escolar: '',
    id_seccion: '',
    id_programa: '',
  });

  // Cargar campus e instituciones una sola vez (para resolver etiquetas en el contexto del reporte)
  useEffect(() => {
    let mounted = true;
    Promise.all([
      getCampus().catch(() => []),
      getInstitutions().catch(() => []),
    ]).then(([cam, inst]) => {
      if (!mounted) return;
      setCampusList(Array.isArray(cam) ? cam : []);
      setInstituciones(Array.isArray(inst) ? inst : []);
    });

    const handleCampusChanged = () => {
      // Re-trigger context resolution on next generate (no action needed here)
    };
    window.addEventListener('campusChanged', handleCampusChanged);
    return () => {
      mounted = false;
      window.removeEventListener('campusChanged', handleCampusChanged);
    };
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Resuelve el contexto completo: los campos ciclo/seccion/programa vienen de FiltrosReporte;
  // campus e institucion se leen del encabezado global (localStorage).
  // NOTA: HeaderGlobal guarda NOMBRES en localStorage, no IDs. Hay que buscar el objeto
  // por nombre para obtener la etiqueta correcta en el encabezado del reporte.
  const handleContextResolved = useCallback((ctx) => {
    const campusName = localStorage.getItem('selectedCampus') || '';
    const institutionName = localStorage.getItem('selectedInstitution') || '';
    const campusObj = campusList.find((c) =>
      c.nombre === campusName || String(c.id) === campusName
    );
    const instObj = instituciones.find((i) =>
      i.name === institutionName || String(i.id) === institutionName
    );
    setContexto({
      ...ctx,
      campus: campusObj ? campusObj.nombre : (campusName || 'Todos'),
      institucion: instObj ? instObj.name : (institutionName || '—'),
    });
  }, [campusList, instituciones]);

  const handleGenerate = useCallback(async () => {
    if (!filters.id_ciclo_escolar) {
      setError('Selecciona un ciclo escolar para continuar.');
      return;
    }
    // HeaderGlobal guarda NOMBRES en localStorage. Resolver a ID numérico
    // buscando en las listas ya cargadas antes de enviar al backend.
    const campusName = localStorage.getItem('selectedCampus') || '';
    const institutionName = localStorage.getItem('selectedInstitution') || '';
    const campusObj = campusList.find((c) =>
      c.nombre === campusName || String(c.id) === campusName
    );
    const instObj = instituciones.find((i) =>
      i.name === institutionName || String(i.id) === institutionName
    );
    const resolvedCampusId = campusObj ? String(campusObj.id) : '';
    const resolvedInstId = instObj ? String(instObj.id) : '';
    setLoading(true);
    setError('');
    try {
      const data = await getReporteEstimacionAlumnado({
        id_ciclo_escolar: filters.id_ciclo_escolar,
        id_campus: resolvedCampusId || undefined,
        id_seccion: filters.id_seccion || undefined,
        id_programa: filters.id_programa || undefined,
        id_institucion: resolvedInstId || undefined,
      });
      setReportData(data);
    } catch (err) {
      console.error('[ReporteEstimacionAlumnado] Error:', err);
      setError(err.message || 'No se pudo obtener el reporte.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExportXLS = useCallback(() => {
    if (!reportData) return;
    exportReporteEstimacionAlumnadoXLS({
      programas: reportData.programas || [],
      contexto,
      estadisticas: reportData.estadisticas || null,
    });
  }, [reportData, contexto]);

  const hasResults =
    reportData &&
    Array.isArray(reportData.programas) &&
    reportData.programas.length > 0;

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal title="Estimación de Alumnado" />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Generando reporte..." />

                <FiltrosReporte
                  filters={filters}
                  onChange={handleFilterChange}
                  onGenerate={handleGenerate}
                  loading={loading}
                  onContextResolved={handleContextResolved}
                />

                {error && (
                  <div
                    style={{
                      background: '#fdecea',
                      color: '#c5221f',
                      padding: '10px 14px',
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 13,
                    }}
                  >
                    {error}
                  </div>
                )}

                {reportData ? (
                  <div className={styles.reportContainer}>
                    <TablaReporte
                      programas={reportData.programas || []}
                      contexto={contexto}
                      estadisticas={reportData.estadisticas || null}
                    />
                    <div className={styles.actionsRow}>
                      <button
                        type="button"
                        className={styles.xlsButton}
                        onClick={handleExportXLS}
                        disabled={!hasResults || loading}
                        title={hasResults ? 'Exportar a XLSX' : 'No hay datos para exportar'}
                      >
                        <span role="img" aria-label="excel">📊</span>
                        Guardar a Excel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.reportContainer}>
                    <div className={styles.emptyState}>
                      <div className={styles.emptyIcon}>🎓</div>
                      <p className={styles.emptyText}>Sin reporte generado</p>
                      <p className={styles.emptySubText}>
                        Selecciona los filtros y presiona{' '}
                        <strong>Generar Reporte</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ReporteEstimacionAlumnadoScreen;
